import { useEffect, useMemo, useState } from 'react';
import { globalBus } from '../streams/wsBus';
import { useActionFeedback } from '../streams/useActionFeedback';

type TaskRow = [string, string, string, string, string];

export function TasksPanel() {
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [filters, setFilters] = useState<{ q?: string; status?: string; owner?: string }>({});
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const { states, fire } = useActionFeedback(globalBus);

  useEffect(() => {
    const unsubscribe = globalBus.subscribe((message) => {
      if (message.t === '__status') {
        setStatus(message.status);
      }
      if (message.t === 'scene' && message.id === 'hivecore.tasks') {
        const list = message.body?.widgets?.find((w: any) => w.key === 'tasks.list')?.rows ?? [];
        setRows(list);
      }
    });
    globalBus.sendIntent('tasks.fetch');
    return unsubscribe;
  }, []);

  const connectionChip = useMemo(() => {
    switch (status) {
      case 'connected': return { label: 'Connected', tone: 'ok' };
      case 'disconnected': return { label: 'Reconnecting…', tone: 'warn' };
      default: return { label: 'Connecting…', tone: 'warn' };
    }
  }, [status]);

  const runCreate = () => fire('tasks.create.btn', 'tasks.create', { title: `Task ${Date.now()}` });
  const runAssign = (id: string) => fire(`tasks.assign.${id}`, 'tasks.assign', { id, owner: 'Operator' });
  const runRun = (id: string) => fire(`tasks.run.${id}`, 'tasks.run', { id });
  const runDelete = (id: string) => fire(`tasks.delete.${id}`, 'tasks.delete', { id });
  const runFilter = () => globalBus.sendIntent('tasks.query', filters);

  return (
    <div className="panel">
      <header className="panel-header">
        <h2>Tasks</h2>
        <span className={`chip ${connectionChip.tone}`}>{connectionChip.label}</span>
        <button data-state={states['tasks.create.btn'] ?? 'idle'} onClick={runCreate}>New Task</button>
      </header>

      <div className="filters" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <input
          placeholder="Search…"
          value={filters.q ?? ''}
          onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
        />
        <input
          placeholder="Owner"
          value={filters.owner ?? ''}
          onChange={(e) => setFilters((prev) => ({ ...prev, owner: e.target.value }))}
        />
        <select
          value={filters.status ?? ''}
          onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value || undefined }))}
        >
          <option value="">All statuses</option>
          <option value="ready">Ready</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
        </select>
        <button onClick={runFilter}>Apply</button>
      </div>

      <table className="table" role="grid">
        <thead>
          <tr>
            <th scope="col">Id</th>
            <th scope="col">Title</th>
            <th scope="col">Status</th>
            <th scope="col">Owner</th>
            <th scope="col">Updated</th>
            <th scope="col" style={{ width: '180px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)' }}>
                No tasks. Create one to get started.
              </td>
            </tr>
          ) : rows.map(([id, title, status, owner, updated]) => (
            <tr key={id}>
              <td>{id.slice(0, 6)}</td>
              <td>{title}</td>
              <td>{status}</td>
              <td>{owner}</td>
              <td>{new Date(updated).toLocaleTimeString()}</td>
              <td style={{ display: 'flex', gap: '6px' }}>
                <button data-state={states[`tasks.assign.${id}`] ?? 'idle'} onClick={() => runAssign(id)}>Assign</button>
                <button data-state={states[`tasks.run.${id}`] ?? 'idle'} onClick={() => runRun(id)}>Run</button>
                <button data-state={states[`tasks.delete.${id}`] ?? 'idle'} onClick={() => runDelete(id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
